import React from "react";
import { Column, useTable, ColumnInstance, useExpanded, PluginHook, Cell } from "react-table";
import { StandardLonghandProperties } from "csstype";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";

interface Props<T extends object> {
  columns: Column<T>[];
  data: T[];
  hasFooter?: boolean;
  loading?: boolean;
  footerStyles?: React.CSSProperties;
  shadeNulls?: boolean;
  subRows?: boolean;
}

export interface CustomColumnInstance<T extends object> extends ColumnInstance<T> {
  hideTitle?: boolean;
  textAlign?: StandardLonghandProperties["textAlign"];
  whiteSpace?: StandardLonghandProperties["whiteSpace"];
  width?: StandardLonghandProperties["width"];
  paddingRight?: StandardLonghandProperties["paddingRight"];
  fontFamily?: StandardLonghandProperties["fontFamily"];
  fontStyleOverride?: (cell: Cell<T, any>) => StandardLonghandProperties["fontStyle"];
  separator?: boolean;
}

const Table = <T extends object>(props: Props<T>) => {
  const { columns, data, loading, hasFooter, footerStyles = {}, subRows } = props;

  const { t } = useTranslation();

  const noData = !loading && data.length === 0;

  const tableColumns = React.useMemo(
    () =>
      loading
        ? columns.map((c) => ({
            ...c,
            Cell: <Skeleton height={13.6} width="80%" />,
            Footer: hasFooter ? <Skeleton height={13.6} width="80%" /> : undefined,
          }))
        : columns,
    [loading, columns, hasFooter]
  );

  const tableData = React.useMemo(() => {
    if (loading && data.length < 1) {
      return [{}, {}, {}, {}, {}] as T[];
    }
    if (noData && !hasFooter) {
      return [
        {
          [tableColumns[0].accessor as string]: t("no_data"),
        },
      ] as T[];
    }
    return data;
  }, [loading, data, noData, t, tableColumns, hasFooter]);

  const hasSubRows = React.useMemo(
    () =>
      subRows && data.length > 0
        ? data.some((d) => (d as any)?.subRows?.length > 0 ?? false)
        : false,
    [data, subRows]
  );

  const plugins = React.useMemo(
    () => (subRows ? ([useExpanded] as PluginHook<T>[]) : ([] as PluginHook<T>[])),
    [subRows]
  );

  const tableColumnsData = React.useMemo(() => {
    const data = [] as Column<T>[];

    if (hasSubRows) {
      data.push({
        id: "expander",
        width: 20,
        Cell: ({ row }: { row: any }) => {
          return row.canExpand ? (
            <span
              {...row.getToggleRowExpandedProps({
                style: {
                  // We can even use the row.depth property
                  // and paddingLeft to indicate the depth
                  // of the row
                  paddingLeft: `${row.depth * 2}rem`,
                },
              })}
            >
              {row.isExpanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </span>
          ) : null;
        },
      });
    }
    return [...data, ...tableColumns];
  }, [tableColumns, hasSubRows]);

  const { getTableProps, getTableBodyProps, headerGroups, footerGroups, rows, prepareRow } =
    useTable(
      {
        columns: tableColumnsData,
        data: tableData,
        getSubRows: subRows ? (row: any) => row.subRows : undefined,
      },
      ...plugins
    );

  return (
    <table {...getTableProps()}>
      <thead>
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => {
              const textAlign = (column as CustomColumnInstance<T>).textAlign;
              const whiteSpace = (column as CustomColumnInstance<T>).whiteSpace;
              const width = (column as CustomColumnInstance<T>).width;
              const minWidth = column.minWidth;
              const paddingRight = (column as CustomColumnInstance<T>).paddingRight;
              const hideTitle = (column as CustomColumnInstance<T>).hideTitle;

              return (
                <th
                  {...column.getHeaderProps()}
                  style={{
                    textAlign,
                    whiteSpace,
                    width,
                    paddingRight,
                    minWidth,
                    visibility: hideTitle ? "hidden" : undefined,
                  }}
                  className={(column as CustomColumnInstance<T>).separator ? "separator" : ""}
                >
                  <b>{column.render("Header")}</b>
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row);
          return (
            <tr {...row.getRowProps()}>
              {row.cells.map((cell, i) => {
                const textAlign = (cell.column as CustomColumnInstance<T>).textAlign;
                const width = (cell.column as CustomColumnInstance<T>).width;
                const minWidth = cell.column.minWidth;
                const whiteSpace = (cell.column as CustomColumnInstance<T>).whiteSpace;
                const paddingRight = (cell.column as CustomColumnInstance<T>).paddingRight;
                const fontFamily = (cell.column as CustomColumnInstance<T>).fontFamily;
                const fontStyleFunction = (cell.column as CustomColumnInstance<T>)
                  .fontStyleOverride;

                const fontStyle = fontStyleFunction?.(cell);

                const noDataStyles =
                  noData && !hasFooter ? { color: "#D3741C", fontStyle: "italic" } : {};

                if (noData && i !== 0) {
                  cell.column.Cell = "";
                }
                let classString = "";
                if (cell.value === null && props.shadeNulls) {
                  classString += "null-cell ";
                }
                if ((cell.column as CustomColumnInstance<T>).separator) {
                  classString += "separator ";
                }

                const isSubRow = (cell.row as any).depth > 0;

                return (
                  <td
                    {...cell.getCellProps()}
                    style={{
                      ...noDataStyles,
                      textAlign,
                      whiteSpace,
                      width,
                      paddingRight,
                      minWidth,
                      fontFamily,
                      paddingLeft: i === 1 && isSubRow ? 8 : undefined,
                      fontStyle: fontStyle ?? (i === 1 && isSubRow ? "italic" : undefined),
                    }}
                    className={classString}
                  >
                    {cell.render("Cell")}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
      {hasFooter && (
        <tfoot>
          {footerGroups.map((group) => {
            return (
              <tr {...group.getFooterGroupProps()}>
                {group.headers.map((column) => {
                  const textAlign = (column as CustomColumnInstance<T>).textAlign;
                  const width = (column as CustomColumnInstance<T>).width;
                  const minWidth = column.minWidth;
                  const whiteSpace = (column as CustomColumnInstance<T>).whiteSpace;
                  const paddingRight = (column as CustomColumnInstance<T>).paddingRight;
                  return (
                    <td
                      {...column.getFooterProps()}
                      style={{
                        textAlign,
                        whiteSpace,
                        width,
                        paddingRight,
                        minWidth,
                        ...footerStyles,
                      }}
                      className={(column as CustomColumnInstance<T>).separator ? "separator" : ""}
                    >
                      {column.render("Footer")}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tfoot>
      )}
    </table>
  );
};

export default React.memo(Table);
